import {
    Alert,
    Box,
    Button,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    Skeleton,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import * as Yup from 'yup';
import {
    Check,
    ChevronDown,
    DollarSign,
    Eye,
    GripVertical,
    Info as InfoIcon,
    ListChecks,
    Pencil,
    Plus,
    Repeat,
    Trash2,
    Wrench,
    X as XIcon,
} from 'lucide-react';
import { httpClient } from '../../common/httpclient';
import { formatCurrency } from '../../common/formatters';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DialogForm from '../../component/dialog/DialogForm';
import Dropdown from '../../component/dropdown/Dropdown';
import BooleanPill from '../../component/common/BooleanPill';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import MobileServiceForm from '../../component/service/MobileServiceForm';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';

// Unidades aceitas pela API para o intervalo de recorrência do serviço.
export const RECURRENCE_UNIT_OPTIONS = [
    { id: 'DAY', name: 'Dia(s)' },
    { id: 'WEEK', name: 'Semana(s)' },
    { id: 'MONTH', name: 'Mês(es)' },
    { id: 'YEAR', name: 'Ano(s)' },
];

// Atalho "+ Nova categoria/subcategoria" abaixo do combobox — mesmo visual de
// `pages/products/Form.js` (`NewInlineLink`), aqui no escopo do módulo para
// não remontar a cada render de `Form`.
function NewInlineLink({ label, onClick, disabled }) {
    return (
        <Box
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            onClick={disabled ? undefined : onClick}
            onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onClick();
                }
            }}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                mt: '6px',
                fontSize: '11.5px',
                fontWeight: 700,
                color: 'primary.main',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.45 : 1,
                '&:hover': { opacity: disabled ? 0.45 : 0.75 },
            }}
        >
            <Plus size={12} strokeWidth={2.5} />
            {label}
        </Box>
    );
}

/**
 * Seção colapsável do formulário de inserção (DESIGN-SYSTEM.md §5.5
 * "Accordion insertion form" / §6.2) — copiada 1:1 de `products/Form.js`
 * (mesmo componente de módulo, fora do render de `Form`, para não perder
 * foco dos inputs a cada re-render).
 */
function AccordionSection({ open, complete, icon: Icon, title, subtitle, onToggle, children }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: '1.5px solid',
                borderColor: open ? 'primary.main' : 'divider',
                borderRadius: `${theme.customRadius.hero}px`,
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                boxShadow: open ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                transition: 'border-color .2s, box-shadow .2s',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 20px', cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                    }}
                >
                    <Icon size={19} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
                </Box>
                {complete && (
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Check size={12} strokeWidth={3} />
                    </Box>
                )}
                <ChevronDown
                    size={16}
                    color={theme.palette.text.disabled}
                    style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                />
            </Box>
            <Box sx={{ maxHeight: open ? 3000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
                <Box sx={{ p: '16px 20px 24px' }}>{children}</Box>
            </Box>
        </Box>
    );
}

function PreviewRow({ label, value, highlight }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
            <span>{label}</span>
            {typeof value === 'string' ? (
                <Box component="b" sx={{ color: highlight ? 'success.main' : 'text.primary' }}>{value}</Box>
            ) : (
                value
            )}
        </Box>
    );
}

export default function Form() {
    const theme = useTheme();
    // Largura real do menu lateral (`ModernSidebar.js`) — usada para alinhar
    // o rodapé de ações fixo (`.action-footer` do protótipo) com a borda do
    // conteúdo em vez da borda da viewport. Ref: DESIGN-SYSTEM.md §3.1.
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell && id ? 'Editar serviço' : '');

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');

    const [loading, setLoading] = useState(false); // salvando (criar/atualizar)
    const [loadingData, setLoadingData] = useState(true); // GET /services/{id} (só relevante em edição)
    // Seções do acordeão abertas de forma independente (não exclusiva): cada
    // seção guarda seu próprio estado de expansão, permitindo múltiplas
    // seções abertas ao mesmo tempo.
    const [openSections, setOpenSections] = useState(['info']);
    const isSectionOpen = (key) => openSections.includes(key);
    const toggleSection = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
    const ensureSectionOpen = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev : [...prev, key]
    ));

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [loadingSubcategories, setLoadingSubcategories] = useState(false);
    // Cadastro rápido de categoria/subcategoria de serviço (dialog), mesmo
    // fluxo de `pages/products/Form.js` mas contra `/category-service`.
    const [newCategoryDialog, setNewCategoryDialog] = useState({ open: false, name: '', description: '', error: '', saving: false });
    const [newSubcategoryDialog, setNewSubcategoryDialog] = useState({ open: false, name: '', description: '', error: '', saving: false });

    const [tasks, setTasks] = useState([]);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [taskForm, setTaskForm] = useState({
        task_name: '',
        task_description: '',
        sequence_order: 0
    });
    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [deletingTask, setDeletingTask] = useState(false);

    // "Usa veículo" (configuração da empresa): a seção de recorrência só existe
    // quando ativa.
    const { settings: enterpriseSettings } = useEnterpriseConfigContext();
    const usesVehicle = Boolean(enterpriseSettings?.os_use_vehicle);

    const validationSchema = Yup.object({
        name: Yup.string().required('Nome é obrigatório'),
        recurrence_interval: Yup.mixed().test(
            'recurrence_interval',
            'Informe um intervalo inteiro maior que 0',
            (value, ctx) => {
                if (!usesVehicle || !ctx.parent.is_recurring) return true;
                const s = String(value ?? '').trim();
                return /^\d+$/.test(s) && parseInt(s, 10) > 0;
            }
        ),
        recurrence_km: Yup.mixed().test(
            'recurrence_km',
            'Informe um número inteiro maior ou igual a 0',
            (value, ctx) => {
                if (!usesVehicle || !ctx.parent.is_recurring) return true;
                const s = String(value ?? '').trim();
                return s === '' || /^\d+$/.test(s);
            }
        ),
        recurrence_reminder_days: Yup.mixed().test(
            'recurrence_reminder_days',
            'Informe um número inteiro maior ou igual a 0',
            (value, ctx) => {
                if (!usesVehicle || !ctx.parent.is_recurring) return true;
                return /^\d+$/.test(String(value ?? '').trim());
            }
        ),
        price: Yup.string()
            .required('Valor é obrigatório')
            .test('price', 'Valor deve ser maior que 0,00', value => {
                if (formik.values.is_generic) {
                    return true;
                }
                if (!value) return true;
                return value !== '0,00';
            }),
        external_price: Yup.string().when('is_external', {
            is: true,
            then: (schema) =>
                schema.test(
                    'external_price',
                    'Valor do custo externo inválido',
                    (value) => {
                        if (!value || !String(value).trim()) return true;
                        const normalized = String(value).trim().replace(/\./g, '').replace(',', '.');
                        const n = parseFloat(normalized);
                        return Number.isFinite(n) && n >= 0;
                    }
                ),
            otherwise: (schema) => schema.notRequired()
        }),
        description: Yup.string().max(100, 'Descrição deve ter no máximo 100 caracteres'),
        category_id: Yup.string().nullable(),
        subcategory_id: Yup.string().nullable()
    });

    const handleShowSuccessSnackbar = () => {
        setOpenSnackbar(true);
        if (id) {
            setMessage('Serviço atualizado com sucesso!');
        } else {
            setMessage('Serviço cadastrado com sucesso!');
        }
        setSeverity('success');
    }

    const apiErrorToString = (responseData) => {
        if (responseData == null) return '';
        if (typeof responseData === 'string') return responseData;
        if (typeof responseData === 'object') {
            return (
                responseData.message ||
                responseData.error ||
                responseData.id ||
                ''
            );
        }
        return String(responseData);
    };

    const handleShowErrorSnackbar = (responseData) => {
        setOpenSnackbar(true);
        const raw = apiErrorToString(responseData);
        if (/external_price/i.test(raw) && /inválido/i.test(raw)) {
            setMessage('Verifique o valor do custo externo.');
        } else {
            setMessage(raw || 'Erro desconhecido');
        }
        setSeverity('error');
    }

    const formik = useFormik({
        initialValues: {
            name: '',
            price: '',
            external_price: '',
            description: '',
            is_generic: false,
            is_external: false,
            use_checklist: false,
            category_id: '',
            subcategory_id: '',
            is_recurring: false,
            recurrence_interval: '6',
            recurrence_interval_unit: 'MONTH',
            recurrence_reminder_days: '7',
            recurrence_km: ''
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            if (id) {
                handleUpdateService();
            } else {
                handleCreateService();
            }
        },
    });

    const buildServicePayload = () => {
        const v = formik.values;
        // Recorrência só é enviada quando a empresa usa veículo e o serviço é recorrente.
        const recurring = usesVehicle && Boolean(v.is_recurring);
        return {
            ...v,
            is_external: Boolean(v.is_external),
            is_generic: Boolean(v.is_generic),
            external_price: v.is_external ? (v.external_price || '').trim() : '',
            is_recurring: recurring,
            recurrence_interval: recurring ? parseInt(v.recurrence_interval, 10) : 0,
            recurrence_interval_unit: recurring ? v.recurrence_interval_unit : '',
            recurrence_reminder_days: recurring ? parseInt(v.recurrence_reminder_days, 10) : 0,
            recurrence_km: recurring ? (parseInt(v.recurrence_km, 10) || 0) : 0
        };
    };

    const handleCreateService = async () => {
        setLoading(true);
        try {
            const response = await httpClient.post('/services', buildServicePayload());
            if (response.status !== 201) {
                const responseData = response.data;
                handleShowErrorSnackbar(responseData);
                return;
            }
            handleShowSuccessSnackbar();
            formik.resetForm();
        } catch (error) {
            console.error('Error creating service:', error);
            handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao criar serviço');
        } finally {
            setLoading(false);
        }
    }

    const handleUpdateService = async () => {
        setLoading(true);
        try {
            const response = await httpClient.put(`/services/${id}`, buildServicePayload());
            if (response.status === 204) {
                handleShowSuccessSnackbar();
            } else {
                handleShowErrorSnackbar(response.data || 'Erro ao atualizar serviço');
            }
        } catch (error) {
            console.error('Error updating service:', error);
            handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao atualizar serviço');
        } finally {
            setLoading(false);
        }
    }

    const loadServiceData = async () => {
        if (!id) return;

        try {
            setLoadingData(true);
            const response = await httpClient.get(`/services/${id}`);
            const serviceData = response.data;
            const categoryId = serviceData.category_id || serviceData.categoryId || serviceData.category || '';
            const subcategoryId = serviceData.subcategory_id || serviceData.subCategoryId || serviceData.subcategory || serviceData.sub_category_id || '';

            // Atualizar os valores do formulário com os dados do serviço
            formik.setValues({
                name: serviceData.name || '',
                price: serviceData.price ? serviceData.price.toString() : '',
                external_price: serviceData.external_price ? serviceData.external_price.toString() : '',
                description: serviceData.description || '',
                is_generic: serviceData.is_generic || false,
                is_external: serviceData.is_external || false,
                use_checklist: serviceData.use_checklist || false,
                category_id: categoryId || '',
                subcategory_id: subcategoryId || '',
                is_recurring: Boolean(serviceData.is_recurring),
                recurrence_interval: serviceData.is_recurring && serviceData.recurrence_interval ? String(serviceData.recurrence_interval) : '6',
                recurrence_interval_unit: serviceData.is_recurring && serviceData.recurrence_interval_unit ? serviceData.recurrence_interval_unit : 'MONTH',
                recurrence_reminder_days: serviceData.is_recurring ? String(serviceData.recurrence_reminder_days ?? 7) : '7',
                recurrence_km: serviceData.is_recurring && serviceData.recurrence_km ? String(serviceData.recurrence_km) : ''
            });

            if (categoryId) {
                await fetchSubcategories(categoryId);
            }
        } catch (error) {
            console.error('Error loading service data:', error);
            handleShowErrorSnackbar('Erro ao carregar dados do serviço');
        } finally {
            setLoadingData(false);
        }
    }

    // Funções para gerenciar tarefas
    const loadTasks = useCallback(async () => {
        if (!id) {
            setTasks([]);
            return;
        }

        try {
            setLoadingTasks(true);
            const response = await httpClient.get(`/services/tasks?serviceId=${id}`);

            if (response.status === 200) {
                // O endpoint retorna um array diretamente
                let tasksData = response.data;

                // Garantir que é um array
                if (!Array.isArray(tasksData)) {
                    console.warn('Expected array but got:', typeof tasksData, tasksData);
                    tasksData = [];
                }

                // Filtrar por service_id (comparação estrita)
                tasksData = tasksData.filter(task => {
                    if (!task || !task.service_id) return false;
                    // Comparar como strings para garantir que funciona
                    return String(task.service_id) === String(id);
                });

                // Ordenar por sequence_order
                const sortedTasks = tasksData.sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));

                setTasks(sortedTasks);
            } else {
                console.warn('Unexpected response status:', response.status);
                setTasks([]);
            }
        } catch (error) {
            console.error('Error loading tasks:', error);
            handleShowErrorSnackbar('Erro ao carregar tarefas');
            setTasks([]); // Garantir que a lista fica vazia em caso de erro
        } finally {
            setLoadingTasks(false);
        }
    }, [id]);

    const fetchCategories = async () => {
        try {
            setLoadingCategories(true);
            const response = await httpClient.get('/category-service/dropdown');
            const responseData = Array.isArray(response.data) ? response.data : [];
            setCategories(responseData);
            return responseData;
        } catch (error) {
            console.error('Error fetching service categories:', error);
            setCategories([]);
            return [];
        } finally {
            setLoadingCategories(false);
        }
    };

    const fetchSubcategories = async (categoryId) => {
        if (!categoryId) {
            setSubcategories([]);
            return [];
        }
        try {
            setLoadingSubcategories(true);
            const response = await httpClient.get(`/category-service/subcategory/dropdown?categoryId=${categoryId}`);
            const responseData = Array.isArray(response.data) ? response.data : [];
            setSubcategories(responseData);
            return responseData;
        } catch (error) {
            console.error('Error fetching service subcategories:', error);
            setSubcategories([]);
            return [];
        } finally {
            setLoadingSubcategories(false);
        }
    };

    const handleChangeCategory = async (value) => {
        formik.setFieldValue('category_id', value);
        formik.setFieldValue('subcategory_id', '');
        await fetchSubcategories(value);
    };

    const handleChangeSubcategory = (value) => {
        formik.setFieldValue('subcategory_id', value);
    };

    const handleCreateCategory = async () => {
        const name = newCategoryDialog.name.trim();
        const description = newCategoryDialog.description.trim();
        if (!name || !description) {
            setNewCategoryDialog((prev) => ({ ...prev, error: 'Nome e descrição são obrigatórios' }));
            return;
        }
        setNewCategoryDialog((prev) => ({ ...prev, saving: true, error: '' }));
        try {
            // `httpClient` nunca rejeita por status HTTP — status checado à mão.
            const response = await httpClient.post('/category-service', { name, description });
            if (response.status !== 201 && response.status !== 200) {
                throw new Error(typeof response.data === 'string' ? response.data : 'Erro ao criar categoria');
            }
            const created = response.data;
            setCategories((prev) => [...prev, { id: created.id, name: created.name || name }]);
            formik.setFieldValue('category_id', created.id);
            formik.setFieldValue('subcategory_id', '');
            setSubcategories([]);
            setNewCategoryDialog({ open: false, name: '', description: '', error: '', saving: false });
        } catch (error) {
            console.error('Error creating service category:', error);
            setNewCategoryDialog((prev) => ({ ...prev, saving: false }));
            setMessage('Erro ao criar categoria');
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleCreateSubcategory = async () => {
        const name = newSubcategoryDialog.name.trim();
        const description = newSubcategoryDialog.description.trim();
        if (!name || !description) {
            setNewSubcategoryDialog((prev) => ({ ...prev, error: 'Nome e descrição são obrigatórios' }));
            return;
        }
        setNewSubcategoryDialog((prev) => ({ ...prev, saving: true, error: '' }));
        try {
            const response = await httpClient.post('/category-service/subcategory', {
                name,
                description,
                category_id: formik.values.category_id,
            });
            if (response.status !== 201 && response.status !== 200) {
                throw new Error(typeof response.data === 'string' ? response.data : 'Erro ao criar subcategoria');
            }
            const created = response.data;
            setSubcategories((prev) => [...prev, { id: created.id, name: created.name || name }]);
            formik.setFieldValue('subcategory_id', created.id);
            setNewSubcategoryDialog({ open: false, name: '', description: '', error: '', saving: false });
        } catch (error) {
            console.error('Error creating service subcategory:', error);
            setNewSubcategoryDialog((prev) => ({ ...prev, saving: false }));
            setMessage('Erro ao criar subcategoria');
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    // Carregar dados do serviço quando o componente for montado e houver um ID
    useEffect(() => {
        if (id) {
            loadServiceData();
            loadTasks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, loadTasks]);

    useEffect(() => {
        fetchCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAddTask = async () => {
        if (!taskForm.task_name.trim()) {
            handleShowErrorSnackbar('Nome da tarefa é obrigatório');
            return;
        }

        if (!id) {
            handleShowErrorSnackbar('É necessário salvar o serviço antes de adicionar tarefas');
            return;
        }

        try {
            const maxOrder = tasks.length > 0 ? Math.max(...tasks.map(t => t.sequence_order || 0)) : -1;
            const taskData = {
                ...taskForm,
                service_id: id,
                sequence_order: maxOrder + 1
            };

            const response = await httpClient.post('/services/tasks', taskData);
            if (response.status === 201 || response.status === 200) {
                // Adicionar a tarefa retornada diretamente à lista
                const newTask = response.data;
                if (newTask) {
                    setTasks(prevTasks => {
                        const updatedTasks = [...prevTasks, newTask];
                        // Ordenar por sequence_order
                        return updatedTasks.sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
                    });
                } else {
                    // Fallback: recarregar a lista se não houver dados na resposta
                    await loadTasks();
                }
                setTaskForm({
                    task_name: '',
                    task_description: '',
                    sequence_order: 0
                });
                setMessage('Tarefa adicionada com sucesso!');
                setSeverity('success');
                setOpenSnackbar(true);
            } else {
                handleShowErrorSnackbar(response.data || 'Erro ao adicionar tarefa');
            }
        } catch (error) {
            console.error('Error adding task:', error);
            handleShowErrorSnackbar('Erro ao adicionar tarefa');
        }
    };

    const handleEditTask = (task) => {
        setEditingTask(task);
        setTaskForm({
            task_name: task.task_name || '',
            task_description: task.task_description || '',
            sequence_order: task.sequence_order || 0
        });
    };

    const handleUpdateTask = async () => {
        if (!taskForm.task_name.trim()) {
            handleShowErrorSnackbar('Nome da tarefa é obrigatório');
            return;
        }

        if (!editingTask) return;

        try {
            const taskData = {
                task_name: taskForm.task_name,
                task_description: taskForm.task_description,
                sequence_order: taskForm.sequence_order
            };

            const response = await httpClient.put(`/services/tasks/${editingTask.id}`, taskData);
            if (response.status === 200 || response.status === 204) {
                await loadTasks();
                setEditingTask(null);
                setTaskForm({
                    task_name: '',
                    task_description: '',
                    sequence_order: 0
                });
                setMessage('Tarefa atualizada com sucesso!');
                setSeverity('success');
                setOpenSnackbar(true);
            } else {
                handleShowErrorSnackbar(response.data || 'Erro ao atualizar tarefa');
            }
        } catch (error) {
            console.error('Error updating task:', error);
            handleShowErrorSnackbar('Erro ao atualizar tarefa');
        }
    };

    const handleDeleteTask = (taskId) => {
        setTaskToDelete(taskId);
        setOpenDialogConfirmDelete(true);
    };

    const handleConfirmDeleteTask = async () => {
        if (!taskToDelete) return;

        setDeletingTask(true);
        try {
            const response = await httpClient.delete(`/services/tasks/${taskToDelete}`);
            if (response.status === 200 || response.status === 204) {
                await loadTasks();
                if (editingTask?.id === taskToDelete) {
                    setEditingTask(null);
                    setTaskForm({
                        task_name: '',
                        task_description: '',
                        sequence_order: 0
                    });
                }
                setMessage('Tarefa excluída com sucesso!');
                setSeverity('success');
                setOpenSnackbar(true);
                setOpenDialogConfirmDelete(false);
                setTaskToDelete(null);
            } else {
                handleShowErrorSnackbar(response.data || 'Erro ao excluir tarefa');
            }
        } catch (error) {
            console.error('Error deleting task:', error);
            handleShowErrorSnackbar('Erro ao excluir tarefa');
        } finally {
            setDeletingTask(false);
        }
    };

    const handleCancelEditTask = () => {
        setEditingTask(null);
        setTaskForm({
            task_name: '',
            task_description: '',
            sequence_order: 0
        });
    };

    const breadcrumbs = [
        {
            title: 'Serviços',
            link: '/services'
        }
    ]

    // ---- Derivados do layout "acordeão + preview ao vivo" (DESIGN-SYSTEM.md
    // §5.5/§6.2, Ref: prototipo-telas-insercao.html "Novo Produto"), aqui numa
    // versão mais enxuta — sem seção de imagem/marketplace, já que serviço não
    // tem esses conceitos. Estrutura/estilos copiados 1:1 de `products/Form.js`.
    const isFieldFilled = (fieldName) => {
        const value = formik.values[fieldName];
        if (value === null || value === undefined) return false;
        if (typeof value === 'boolean') return true;
        return String(value).trim().length > 0;
    };

    const isEditing = Boolean(id);

    const standaloneSections = [
        {
            key: 'info',
            title: 'Informações básicas',
            subtitle: 'Nome, categoria e descrição do serviço',
            icon: InfoIcon,
            requiredFields: ['name'],
        },
        {
            key: 'price',
            title: 'Valor e custos',
            subtitle: 'Preço de venda e serviço externo',
            icon: DollarSign,
            requiredFields: ['price'],
        },
        ...(usesVehicle ? [{
            key: 'recurrence',
            title: 'Recorrência',
            subtitle: 'Período de repetição e lembrete ao cliente',
            icon: Repeat,
            requiredFields: [],
        }] : []),
        ...(isEditing ? [{
            key: 'tasks',
            title: 'Tarefas do serviço',
            subtitle: 'Checklist de etapas executadas neste serviço',
            icon: ListChecks,
            requiredFields: [],
        }] : []),
    ];
    const requiredStandaloneSections = standaloneSections.filter((sec) => sec.requiredFields.length > 0);
    const completeStandaloneSectionsCount = requiredStandaloneSections.filter(
        (sec) => sec.requiredFields.every(isFieldFilled)
    ).length;
    const progressPct = requiredStandaloneSections.length > 0
        ? Math.round((completeStandaloneSectionsCount / requiredStandaloneSections.length) * 100)
        : 0;
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    const parseBRLNumber = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        const normalized = String(value).replace(/\./g, '').replace(',', '.');
        const parsed = parseFloat(normalized);
        return Number.isNaN(parsed) ? 0 : parsed;
    };
    const priceNumberValue = parseBRLNumber(formik.values.price);
    const externalPriceNumberValue = parseBRLNumber(formik.values.external_price);
    const externalMarginPct = (formik.values.is_external && priceNumberValue > 0)
        ? Math.max(0, Math.min(100, ((priceNumberValue - externalPriceNumberValue) / priceNumberValue) * 100))
        : 0;
    const externalMarginLabel = (formik.values.is_external && priceNumberValue > 0) ? `${externalMarginPct.toFixed(1)}%` : '—';
    const priceDisplay = formik.values.price ? `R$ ${formik.values.price}` : 'R$ 0,00';

    const findOptionLabel = (list, selectedId) => {
        const found = (list || []).find((item) => String(item.id) === String(selectedId));
        return found ? (found.name || found.Name || '') : '';
    };
    const selectedCategoryLabel = findOptionLabel(categories, formik.values.category_id);
    const selectedSubcategoryLabel = findOptionLabel(subcategories, formik.values.subcategory_id);

    // Mapa campo → seção do acordeão, usado só para reabrir automaticamente a
    // seção com erro ao tentar salvar (uma seção fechada não pode "esconder"
    // um erro de validação do usuário).
    const standaloneFieldSectionMap = {
        name: 'info', category_id: 'info', subcategory_id: 'info', description: 'info',
        price: 'price', external_price: 'price',
        recurrence_interval: 'recurrence', recurrence_reminder_days: 'recurrence', recurrence_km: 'recurrence',
    };
    const handleStandaloneSaveClick = async () => {
        const errors = await formik.validateForm();
        const errorFieldKeys = Object.keys(errors);
        if (errorFieldKeys.length > 0) {
            const sectionKeyWithError = errorFieldKeys
                .map((key) => standaloneFieldSectionMap[key])
                .find(Boolean);
            if (sectionKeyWithError) {
                ensureSectionOpen(sectionKeyWithError);
            }
        }
        formik.handleSubmit();
    };

    // Ref: prototipo-telas-insercao.html `.ff-input`/`.ffs select` — mesma
    // caixa de campo (52px, radius 12px, fundo surface-2, borda 1.5px) e
    // label flutuante (DESIGN-SYSTEM.md §5.5) de `products/Form.js`.
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
            '&.MuiInputBase-multiline': {
                height: 'auto',
                minHeight: '96px',
                alignItems: 'flex-start',
                padding: '24px 14px 7px 14px',
            },
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiSelect-select.MuiOutlinedInput-input': {
            padding: '0 14px',
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
        '& .MuiInputBase-multiline textarea': {
            resize: 'vertical',
        },
    };

    const standaloneFieldSx = {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };

    const requiredFieldLabel = (text) => (
        <>
            {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
        </>
    );

    const isLoadingInput = loadingCategories || (isEditing && loadingData);

    const formContent = (
        <>
            {isMobileShell ? (
                <MobileServiceForm
                    formik={formik}
                    isEditing={isEditing}
                    usesVehicle={usesVehicle}
                    categories={categories}
                    subcategories={subcategories}
                    loadingSubcategories={loadingSubcategories}
                    onChangeCategory={handleChangeCategory}
                    onChangeSubcategory={handleChangeSubcategory}
                    onNewCategory={() => setNewCategoryDialog({ open: true, name: '', description: '', error: '', saving: false })}
                    onNewSubcategory={() => setNewSubcategoryDialog({ open: true, name: '', description: '', error: '', saving: false })}
                    onChangePrice={(e) => formik.setFieldValue('price', formatCurrency(e.target.value))}
                    onChangeExternalPrice={(e) => formik.setFieldValue('external_price', formatCurrency(e.target.value))}
                    onToggleGeneric={(checked) => {
                        formik.setFieldValue('is_generic', checked);
                        formik.setFieldValue('price', checked ? '0,00' : '');
                    }}
                    onToggleExternal={(on) => {
                        formik.setFieldValue('is_external', on);
                        if (!on) formik.setFieldValue('external_price', '');
                    }}
                    tasks={{
                        items: tasks,
                        loading: loadingTasks,
                        form: taskForm,
                        onFormChange: setTaskForm,
                        editing: Boolean(editingTask),
                        onAdd: handleAddTask,
                        onUpdate: handleUpdateTask,
                        onEdit: handleEditTask,
                        onDelete: handleDeleteTask,
                        onCancelEdit: handleCancelEditTask,
                    }}
                    onCancel={() => navigate('/services')}
                    loading={loading}
                />
            ) : (
            <Box sx={{ px: 0, pb: 0 }}>
                <Box sx={{ mb: 2.5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '20px',
                            flexWrap: 'wrap',
                            mb: 2.25,
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                                {id ? 'Editar serviço' : 'Novo serviço'}
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                                {id
                                    ? 'Atualize as seções do serviço — o card ao lado mostra como ele aparece na venda.'
                                    : 'Cadastre por seções — o card ao lado é exatamente como aparece na tela de venda.'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                            <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                                <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                    <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                    <circle
                                        cx="27"
                                        cy="27"
                                        r={RING_R}
                                        fill="none"
                                        strokeWidth="5"
                                        strokeLinecap="round"
                                        stroke={theme.palette.primary.main}
                                        strokeDasharray={RING_CIRC}
                                        strokeDashoffset={ringOffset}
                                        style={{ transition: 'stroke-dashoffset .35s ease' }}
                                    />
                                </svg>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                        fontWeight: 800,
                                    }}
                                >
                                    {progressPct}%
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cadastro</Typography>
                                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                    completo conforme você preenche
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {standaloneSections.map((sec, idx) => {
                            const isSecOpen = isSectionOpen(sec.key);
                            const isSecComplete = sec.requiredFields.length > 0 && sec.requiredFields.every(isFieldFilled);
                            return (
                                <Box
                                    key={sec.key}
                                    onClick={() => ensureSectionOpen(sec.key)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '7px',
                                        px: '14px',
                                        py: '8px',
                                        borderRadius: '24px',
                                        border: '1.5px solid',
                                        borderColor: isSecOpen ? 'primary.main' : 'divider',
                                        backgroundColor: isSecOpen ? 'info.bg' : 'background.paper',
                                        color: isSecOpen ? 'primary.main' : 'text.secondary',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 17,
                                            height: 17,
                                            borderRadius: '50%',
                                            border: '2px solid',
                                            borderColor: isSecComplete ? 'success.main' : (isSecOpen ? 'primary.main' : 'text.disabled'),
                                            backgroundColor: isSecComplete ? 'success.main' : 'transparent',
                                            color: isSecComplete ? '#fff' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 9,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {isSecComplete ? <Check size={10} strokeWidth={3} /> : idx + 1}
                                    </Box>
                                    {sec.title}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <form onSubmit={formik.handleSubmit}>
                    {isLoadingInput ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                            <Stack spacing={1.75}>
                                <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                                <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                            </Stack>
                            <Skeleton variant="rounded" height={340} sx={{ borderRadius: '20px' }} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                            <Stack spacing={1.75} sx={{ minWidth: 0 }}>
                                <AccordionSection
                                    open={isSectionOpen('info')}
                                    complete={standaloneSections[0].requiredFields.every(isFieldFilled)}
                                    icon={InfoIcon}
                                    title="Informações básicas"
                                    subtitle="Nome, categoria e descrição do serviço"
                                    onToggle={() => toggleSection('info')}
                                >
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2, alignItems: 'start', '& > *': { minWidth: 0 } }}>
                                        <Box>
                                            <Dropdown
                                                label="Categoria"
                                                labelVariant="static-micro"
                                                data={categories}
                                                selectedItem={formik.values.category_id}
                                                handleChange={handleChangeCategory}
                                                error={formik.touched.category_id && Boolean(formik.errors.category_id)}
                                                helperText={formik.touched.category_id && formik.errors.category_id}
                                                variant="outlined"
                                                size="medium"
                                                sx={standaloneFieldSx}
                                            />
                                            <NewInlineLink
                                                label="Nova categoria"
                                                onClick={() => setNewCategoryDialog({ open: true, name: '', description: '', error: '', saving: false })}
                                            />
                                        </Box>
                                        <Box>
                                            <Dropdown
                                                label="Subcategoria"
                                                labelVariant="static-micro"
                                                data={subcategories}
                                                selectedItem={formik.values.subcategory_id}
                                                handleChange={handleChangeSubcategory}
                                                variant="outlined"
                                                size="medium"
                                                disabled={!formik.values.category_id}
                                                loading={loadingSubcategories}
                                                helperText={!formik.values.category_id ? 'Selecione uma categoria primeiro' : ''}
                                                sx={standaloneFieldSx}
                                            />
                                            <NewInlineLink
                                                label="Nova subcategoria"
                                                disabled={!formik.values.category_id}
                                                onClick={() => setNewSubcategoryDialog({ open: true, name: '', description: '', error: '', saving: false })}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ mb: 2 }}>
                                        <TextField
                                            autoFocus
                                            label={requiredFieldLabel('Nome do Serviço')}
                                            fullWidth
                                            name="name"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            error={formik.touched.name && Boolean(formik.errors.name)}
                                            helperText={formik.touched.name && formik.errors.name}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                    </Box>
                                    <Box>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            label="Descrição do Serviço (opcional)"
                                            name="description"
                                            value={formik.values.description}
                                            onChange={formik.handleChange}
                                            error={formik.touched.description && Boolean(formik.errors.description)}
                                            helperText={formik.touched.description && formik.errors.description}
                                            inputProps={{ maxLength: 100 }}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5, textAlign: 'right' }}>
                                            {(formik.values.description || '').length}/100
                                        </Typography>
                                    </Box>
                                </AccordionSection>

                                <AccordionSection
                                    open={isSectionOpen('price')}
                                    complete={standaloneSections[1].requiredFields.every(isFieldFilled)}
                                    icon={DollarSign}
                                    title="Valor e custos"
                                    subtitle="Preço de venda e serviço externo"
                                    onToggle={() => toggleSection('price')}
                                >
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: formik.values.is_external ? '1fr 1fr' : '1fr' }, gap: 2, mb: 2, '& > *': { minWidth: 0 } }}>
                                        <TextField
                                            label={requiredFieldLabel('Valor (R$)')}
                                            fullWidth
                                            name="price"
                                            value={formik.values.price}
                                            onChange={(e) => {
                                                const value = formatCurrency(e.target.value);
                                                formik.setFieldValue('price', value);
                                            }}
                                            error={formik.touched.price && Boolean(formik.errors.price)}
                                            helperText={formik.touched.price && formik.errors.price}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <DollarSign size={16} color={theme.palette.text.disabled} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        {formik.values.is_external && (
                                            <TextField
                                                label="Custo externo (R$)"
                                                fullWidth
                                                name="external_price"
                                                value={formik.values.external_price}
                                                onChange={(e) => {
                                                    const value = formatCurrency(e.target.value);
                                                    formik.setFieldValue('external_price', value);
                                                }}
                                                error={formik.touched.external_price && Boolean(formik.errors.external_price)}
                                                helperText={
                                                    formik.touched.external_price && formik.errors.external_price
                                                        ? formik.errors.external_price
                                                        : 'Valor pago à empresa que executou o serviço.'
                                                }
                                                variant="outlined"
                                                sx={standaloneFieldSx}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <DollarSign size={16} color={theme.palette.text.disabled} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '14px',
                                            pt: '14px',
                                            borderTop: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Serviço genérico</Typography>
                                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                                {formik.values.is_generic
                                                    ? 'Sem valor fixo — definido na hora da venda'
                                                    : 'Ative para serviços sem um valor fixo padrão'}
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={formik.values.is_generic}
                                            onChange={(e) => {
                                                formik.setFieldValue('is_generic', e.target.checked);
                                                if (e.target.checked) {
                                                    formik.setFieldValue('price', '0,00');
                                                } else {
                                                    formik.setFieldValue('price', '');
                                                }
                                            }}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '14px',
                                            pt: '14px',
                                            mt: 2,
                                            borderTop: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Serviço externo</Typography>
                                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                                {formik.values.is_external
                                                    ? 'Executado por empresa externa; informe o custo pago a ela.'
                                                    : 'Ative quando o serviço é terceirizado a outra empresa'}
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={formik.values.is_external}
                                            onChange={(e) => {
                                                const on = e.target.checked;
                                                formik.setFieldValue('is_external', on);
                                                if (!on) {
                                                    formik.setFieldValue('external_price', '');
                                                }
                                            }}
                                        />
                                    </Box>
                                </AccordionSection>

                                {usesVehicle && (
                                    <AccordionSection
                                        open={isSectionOpen('recurrence')}
                                        complete={false}
                                        icon={Repeat}
                                        title="Recorrência"
                                        subtitle="Período de repetição e lembrete ao cliente"
                                        onToggle={() => toggleSection('recurrence')}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '14px',
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Serviço recorrente</Typography>
                                                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                                    {formik.values.is_recurring
                                                        ? 'Este serviço deve ser repetido periodicamente no veículo.'
                                                        : 'Ative para serviços que se repetem em intervalos fixos (ex.: troca de óleo)'}
                                                </Typography>
                                            </Box>
                                            <Switch
                                                checked={formik.values.is_recurring}
                                                onChange={(e) => formik.setFieldValue('is_recurring', e.target.checked)}
                                            />
                                        </Box>

                                        {formik.values.is_recurring && (
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                                                    gap: 2,
                                                    mt: 2,
                                                    alignItems: 'start',
                                                    '& > *': { minWidth: 0 },
                                                }}
                                            >
                                                <TextField
                                                    label={requiredFieldLabel('Intervalo')}
                                                    fullWidth
                                                    name="recurrence_interval"
                                                    type="number"
                                                    value={formik.values.recurrence_interval}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.recurrence_interval && Boolean(formik.errors.recurrence_interval)}
                                                    helperText={formik.touched.recurrence_interval && formik.errors.recurrence_interval}
                                                    inputProps={{ min: 1, step: 1 }}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <TextField
                                                    select
                                                    label="Unidade"
                                                    fullWidth
                                                    name="recurrence_interval_unit"
                                                    value={formik.values.recurrence_interval_unit}
                                                    onChange={formik.handleChange}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                >
                                                    {RECURRENCE_UNIT_OPTIONS.map((opt) => (
                                                        <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    label="KM (opcional)"
                                                    fullWidth
                                                    name="recurrence_km"
                                                    type="number"
                                                    value={formik.values.recurrence_km}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.recurrence_km && Boolean(formik.errors.recurrence_km)}
                                                    helperText={formik.touched.recurrence_km && formik.errors.recurrence_km}
                                                    inputProps={{ min: 0, step: 1 }}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <TextField
                                                    label={requiredFieldLabel('Lembrete (dias antes)')}
                                                    fullWidth
                                                    name="recurrence_reminder_days"
                                                    type="number"
                                                    value={formik.values.recurrence_reminder_days}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.recurrence_reminder_days && Boolean(formik.errors.recurrence_reminder_days)}
                                                    helperText={formik.touched.recurrence_reminder_days && formik.errors.recurrence_reminder_days}
                                                    inputProps={{ min: 0, step: 1 }}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                            </Box>
                                        )}
                                    </AccordionSection>
                                )}

                                {isEditing && (
                                    <AccordionSection
                                        open={isSectionOpen('tasks')}
                                        complete={false}
                                        icon={ListChecks}
                                        title="Tarefas do serviço"
                                        subtitle="Checklist de etapas executadas neste serviço"
                                        onToggle={() => toggleSection('tasks')}
                                    >
                                        <Box
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: '14px',
                                                backgroundColor: 'background.subtle',
                                            }}
                                        >
                                            <Stack spacing={1.5}>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '3fr 1fr' }, gap: 1.5 }}>
                                                    <TextField
                                                        label="Nome da Tarefa"
                                                        required
                                                        fullWidth
                                                        size="small"
                                                        value={taskForm.task_name}
                                                        onChange={(e) => setTaskForm({ ...taskForm, task_name: e.target.value })}
                                                        variant="outlined"
                                                        sx={standaloneFieldSx}
                                                    />
                                                    <TextField
                                                        label="Ordem"
                                                        type="number"
                                                        size="small"
                                                        value={taskForm.sequence_order}
                                                        onChange={(e) => setTaskForm({ ...taskForm, sequence_order: parseInt(e.target.value) || 0 })}
                                                        variant="outlined"
                                                        inputProps={{ min: 0 }}
                                                        sx={standaloneFieldSx}
                                                    />
                                                </Box>
                                                <TextField
                                                    label="Descrição da Tarefa"
                                                    fullWidth
                                                    multiline
                                                    minRows={2}
                                                    size="small"
                                                    value={taskForm.task_description}
                                                    onChange={(e) => setTaskForm({ ...taskForm, task_description: e.target.value })}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                                    {editingTask && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<XIcon size={14} />}
                                                            onClick={handleCancelEditTask}
                                                        >
                                                            Cancelar
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={editingTask ? <Check size={14} /> : <Plus size={14} />}
                                                        onClick={editingTask ? handleUpdateTask : handleAddTask}
                                                        disabled={loadingTasks}
                                                    >
                                                        {editingTask ? 'Atualizar Tarefa' : 'Adicionar Tarefa'}
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        </Box>

                                        {loadingTasks ? (
                                            <Stack spacing={1}>
                                                <Skeleton variant="rounded" height={56} sx={{ borderRadius: '12px' }} />
                                                <Skeleton variant="rounded" height={56} sx={{ borderRadius: '12px' }} />
                                            </Stack>
                                        ) : tasks.length === 0 ? (
                                            <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                                Nenhuma tarefa cadastrada. Adicione tarefas para este serviço.
                                            </Alert>
                                        ) : (
                                            <List
                                                sx={{
                                                    p: 0,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: '14px',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {tasks.map((task, index) => (
                                                    <React.Fragment key={task.id}>
                                                        <ListItem
                                                            sx={{
                                                                py: 1.25,
                                                                '&:hover': { backgroundColor: 'action.hover' },
                                                            }}
                                                        >
                                                            <GripVertical
                                                                size={16}
                                                                color={theme.palette.text.disabled}
                                                                style={{ marginRight: 12, flexShrink: 0, cursor: 'grab' }}
                                                            />
                                                            <ListItemText
                                                                primary={
                                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700 }}>
                                                                            {task.task_name}
                                                                        </Typography>
                                                                        <Box
                                                                            component="span"
                                                                            sx={{
                                                                                fontSize: '10.5px',
                                                                                fontWeight: 700,
                                                                                px: '8px',
                                                                                py: '2px',
                                                                                borderRadius: '20px',
                                                                                bgcolor: 'info.bg',
                                                                                color: 'primary.main',
                                                                            }}
                                                                        >
                                                                            Ordem: {task.sequence_order}
                                                                        </Box>
                                                                    </Stack>
                                                                }
                                                                secondary={
                                                                    task.task_description ? (
                                                                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                                                                            {task.task_description}
                                                                        </Typography>
                                                                    ) : null
                                                                }
                                                            />
                                                            <ListItemSecondaryAction>
                                                                <Stack direction="row" spacing={0.5}>
                                                                    <Tooltip title="Editar">
                                                                        <IconButton size="small" onClick={() => handleEditTask(task)} sx={{ color: 'primary.main' }}>
                                                                            <Pencil size={15} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Excluir">
                                                                        <IconButton size="small" onClick={() => handleDeleteTask(task.id)} sx={{ color: 'error.main' }}>
                                                                            <Trash2 size={15} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </ListItemSecondaryAction>
                                                        </ListItem>
                                                        {index < tasks.length - 1 && <Divider />}
                                                    </React.Fragment>
                                                ))}
                                            </List>
                                        )}
                                    </AccordionSection>
                                )}
                            </Stack>

                            <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontSize: '10.5px',
                                        fontWeight: 800,
                                        textTransform: 'uppercase',
                                        letterSpacing: '.06em',
                                        color: 'text.disabled',
                                        mb: 1.25,
                                    }}
                                >
                                    <Eye size={13} /> Como aparece na venda
                                </Typography>

                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: `${theme.customRadius.hero - 2}px`,
                                        backgroundColor: 'background.paper',
                                        p: 2,
                                        mb: 1.75,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: '12px',
                                            mb: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'info.bg',
                                        }}
                                    >
                                        <Wrench size={20} color={theme.palette.primary.main} />
                                    </Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.75, minHeight: 36 }}>
                                        {formik.values.name || 'Nome do serviço'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main' }}>
                                            {priceDisplay}
                                        </Typography>
                                        {formik.values.is_generic && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    px: '9px',
                                                    py: '3px',
                                                    borderRadius: '20px',
                                                    backgroundColor: 'warning.bg',
                                                    color: 'warning.main',
                                                }}
                                            >
                                                Genérico
                                            </Box>
                                        )}
                                    </Box>
                                    {formik.values.is_external && (
                                        <>
                                            <Typography sx={{ fontSize: 11, color: 'text.disabled', mb: 0.5 }}>
                                                Margem sobre o custo externo: <Box component="b" sx={{ color: 'text.primary' }}>{externalMarginLabel}</Box>
                                            </Typography>
                                            <Box sx={{ height: 8, borderRadius: '6px', backgroundColor: 'background.subtle', overflow: 'hidden' }}>
                                                <Box
                                                    sx={{
                                                        height: '100%',
                                                        width: `${externalMarginPct}%`,
                                                        borderRadius: '6px',
                                                        background: `linear-gradient(90deg, ${theme.palette.brand2.main}, ${theme.palette.success.main})`,
                                                        transition: 'width .3s ease',
                                                    }}
                                                />
                                            </Box>
                                        </>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: `${theme.customRadius.hero}px`,
                                        backgroundColor: 'background.paper',
                                        boxShadow: theme.customShadows.md,
                                        p: '18px 20px',
                                        mb: 1.75,
                                    }}
                                >
                                    <PreviewRow label="Categoria" value={selectedCategoryLabel || '—'} />
                                    <PreviewRow label="Subcategoria" value={selectedSubcategoryLabel || '—'} />
                                    <PreviewRow label="Genérico" value={<BooleanPill value={formik.values.is_generic} />} />
                                    <PreviewRow label="Serviço externo" value={<BooleanPill value={formik.values.is_external} />} />
                                    <PreviewRow
                                        label="Custo externo"
                                        value={formik.values.is_external ? `R$ ${formik.values.external_price || '0,00'}` : '—'}
                                    />
                                </Box>

                                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                                    Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios.
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Espaçador para compensar o rodapé fixo abaixo. */}
                    <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                    {/* Ref: prototipo-telas-insercao.html `.action-footer` —
                        barra fixa na base da viewport, acompanhando a largura
                        real do menu lateral. */}
                    <Box
                        sx={{
                            position: 'fixed',
                            left: actionFooterLeft,
                            right: 0,
                            bottom: 0,
                            zIndex: theme.zIndex.appBar,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            backgroundColor: 'background.paper',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                            px: 3,
                            py: 1.75,
                            pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                            pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                            pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                            transition: 'left .2s ease',
                        }}
                    >
                        <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                            Revise os dados antes de salvar.
                        </Typography>
                        <Stack direction="row" spacing={1.5}>
                            <Button variant="outlined" onClick={() => navigate('/services')}>
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleStandaloneSaveClick}
                                disabled={loading}
                            >
                                {loading ? 'Salvando...' : (id ? 'Atualizar serviço' : 'Salvar serviço')}
                            </Button>
                        </Stack>
                    </Box>
                </form>
            </Box>
            )}

            <DialogForm
                iconType={'warning'}
                open={openDialogConfirmDelete}
                title={'Atenção'}
                confirmButtonText={'Sim, Excluir'}
                content={'Tem certeza que deseja excluir esta tarefa?'}
                secondaryContent={'Esta ação não pode ser desfeita. A tarefa será removida permanentemente do sistema.'}
                onSubmit={() => {
                    handleConfirmDeleteTask();
                }}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setTaskToDelete(null);
                }}
                loading={deletingTask}
                loadingText="Removendo..."
            />

            <DialogForm
                open={newCategoryDialog.open}
                onClose={() => setNewCategoryDialog((prev) => ({ ...prev, open: false }))}
                title="Nova Categoria"
                content="Preencha os campos abaixo para criar uma nova categoria de serviço."
                onSubmit={handleCreateCategory}
                loading={newCategoryDialog.saving}
            >
                <TextField
                    autoFocus
                    label="Nome da Categoria"
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    value={newCategoryDialog.name}
                    onChange={(e) => setNewCategoryDialog((prev) => ({ ...prev, name: e.target.value, error: '' }))}
                    error={Boolean(newCategoryDialog.error) && !newCategoryDialog.name.trim()}
                    helperText={newCategoryDialog.error && !newCategoryDialog.name.trim() ? newCategoryDialog.error : ''}
                />
                <TextField
                    label="Descrição da Categoria"
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    value={newCategoryDialog.description}
                    onChange={(e) => setNewCategoryDialog((prev) => ({ ...prev, description: e.target.value, error: '' }))}
                    error={Boolean(newCategoryDialog.error) && !newCategoryDialog.description.trim()}
                    helperText={newCategoryDialog.error && !newCategoryDialog.description.trim() ? newCategoryDialog.error : ''}
                />
            </DialogForm>

            <DialogForm
                open={newSubcategoryDialog.open}
                onClose={() => setNewSubcategoryDialog((prev) => ({ ...prev, open: false }))}
                title="Nova Subcategoria"
                content={`Preencha os campos abaixo para criar uma nova subcategoria${selectedCategoryLabel ? ` em "${selectedCategoryLabel}"` : ''}.`}
                onSubmit={handleCreateSubcategory}
                loading={newSubcategoryDialog.saving}
            >
                <TextField
                    autoFocus
                    label="Nome da Subcategoria"
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    value={newSubcategoryDialog.name}
                    onChange={(e) => setNewSubcategoryDialog((prev) => ({ ...prev, name: e.target.value, error: '' }))}
                    error={Boolean(newSubcategoryDialog.error) && !newSubcategoryDialog.name.trim()}
                    helperText={newSubcategoryDialog.error && !newSubcategoryDialog.name.trim() ? newSubcategoryDialog.error : ''}
                />
                <TextField
                    label="Descrição da Subcategoria"
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    value={newSubcategoryDialog.description}
                    onChange={(e) => setNewSubcategoryDialog((prev) => ({ ...prev, description: e.target.value, error: '' }))}
                    error={Boolean(newSubcategoryDialog.error) && !newSubcategoryDialog.description.trim()}
                    helperText={newSubcategoryDialog.error && !newSubcategoryDialog.description.trim() ? newSubcategoryDialog.error : ''}
                />
            </DialogForm>
        </>
    );

    return (
        <PageScaffold
            loading={loading}
            title={id ? 'Editar Serviço' : 'Novo Serviço'}
            titlePageTarget={id ? 'Editar' : 'Novo'}
            links={breadcrumbs}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            {formContent}
        </PageScaffold>
    );
}
